import * as React from 'react'
import type { WallClock } from '@lib/validators/temporal.validator'
import { fmt12 } from '@/lib/eventTime'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { CalendarDays, Clock, Funnel, Megaphone, Settings, TriangleAlert, Users } from 'lucide-react'
import { useVenue } from '@/hooks/useVenues'
import { useOrganization } from '@/hooks/useOrganization'
import { useVenueCalendar } from '@/hooks/useEvents'
import { RecordHeader } from '@/components/RecordHeader'
import { Button } from '@/components/ui/button'
import { LoginToVenueButton } from '@/components/LoginToVenueButton'
import { showsVenueContextHero } from '@/components/VenueContextHero'
import { useAuth } from '@/auth/AuthContext'
import { can } from '@/auth/access'
import { seesVenueSettings } from '@/components/appNav'
import { PageContainer } from '@/components/ui/page-container'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { keyFor, todayKey } from '@/lib/monthGrid'
import { parseUtcDay } from '@lib/helpers/calendar-day'
import { calendarDay, type CalendarDay } from '@lib/validators/temporal.validator'
import { ATTENTION_LABELS, attentionItems, upcoming } from '@/lib/venueAttention'
import type { CalendarEventDto } from '@lib/dto/venue-calendar.dto'
import { venueFacts } from '@/lib/venueFacts'
import { LeadsPanel } from '@/components/lead-stats/LeadsPanel'
import { PipelinePanel } from '@/components/pipeline/PipelinePanel'
import { useGhlAvailability } from '@/hooks/useGoHighLevel'

/** How far ahead "Needs attention" and "Next up" look, in days. */
const RAIL_DAYS = 60

/** The settings sections that used to live under `/venues/:id#…`. */
const SETTINGS_HASHES = new Set([
  'settings',
  'photo',
  'spaces',
  'tracks',
  'inventory',
  'vendors',
  'managers',
  'details',
  'danger',
])

/** `Sat 17 Oct` — a day a venue reads, without the year it already knows. */
function dayLabel(day: CalendarDay): string {
  // Parsed and formatted in UTC: this is a calendar day, not an instant, and
  // rendering it in a negative-offset zone would show the day before.
  return parseUtcDay(day).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

/**
 * The start time as the venue keeps it, or a note when there is none.
 *
 * Wall clock, not the viewer's zone — see `timeLabel` in `VenueCalendar` for
 * what converting it did.
 */
function timeLabel(startsAt: WallClock | null): string {
  if (startsAt === null) return 'No time set'
  return fmt12(startsAt) || 'No time set'
}

/**
 * A venue's own page: how it is doing, and what still needs doing.
 *
 * The calendar used to fill it; it has its own page now (VenueCalendarPage), and
 * the dashboard leads with the venue's lead stats where the venue has
 * GoHighLevel connected (docs/gohighlevel-stats.spec.md). What stays is what a calendar
 * cannot say: the loose ends, because an unconfirmed plan is invisible until the
 * day it bites, and what is next, because that is the question anyone opening
 * this page came to ask.
 */
export function VenueDashboardPage() {
  const { venueId } = useParams()
  const id = venueId ?? ''
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const bandShown = showsVenueContextHero(user)
  // Each part of the page is for those whose role sees it: the venue's ads, its
  // lead stats, and its events (the calendar, what needs attention, what's next).
  const organizationQuery = useOrganization()
  const hasAds = can(user, 'ads:view') && (organizationQuery.data?.features.includes('ads') ?? false)
  const seesEvents = can(user, 'events:view')
  const seesSettings = seesVenueSettings(user)
  // Lead stats lead the dashboard of a venue with GoHighLevel connected (docs/gohighlevel-stats.spec.md).
  // A company turns GoHighLevel on for the venues that use it, so the rest show no
  // sign of it, not even a placeholder while the answer loads.
  const companyHasGoHighLevel = organizationQuery.data?.features.includes('gohighlevel') ?? false
  const ghlAvailability = useGhlAvailability(id, companyHasGoHighLevel)
  const hasGoHighLevel = companyHasGoHighLevel && ghlAvailability.data?.connected === true && can(user, 'leadStats:view')
  // A HoneyBook venue's pipeline, the same way: the panel itself asks whether
  // the venue has HoneyBook, and shows nothing when it doesn't.
  const mayHaveHoneyBook = (organizationQuery.data?.features.includes('honeybook') ?? false) && can(user, 'leadStats:view')

  const venueQuery = useVenue(id)

  // Days from today, not a month: at the end of October "what's next" is in
  // November, and a month-bounded answer would be silently wrong.
  const railRange = React.useMemo(() => {
    const now = new Date()
    const from = keyFor(now.getFullYear(), now.getMonth(), now.getDate())
    const end = new Date(now.getTime() + RAIL_DAYS * 86_400_000)
    const to = keyFor(end.getFullYear(), end.getMonth(), end.getDate())
    return { from: calendarDay(from), to: calendarDay(to) }
  }, [])
  const railQuery = useVenueCalendar({ venueId: id, from: railRange.from, to: railRange.to }, seesEvents)

  const today = todayKey()
  // Held in a memo of its own: a fresh `[]` on every render would make the three
  // derivations below recompute forever.
  const railEvents: CalendarEventDto[] = React.useMemo(
    () => railQuery.data?.events ?? [],
    [railQuery.data],
  )
  const attention = React.useMemo(() => attentionItems(railEvents, today), [railEvents, today])
  const next = React.useMemo(() => upcoming(railEvents, today), [railEvents, today])

  // Deep links from before this page existed pointed at `/venues/:id#spaces` and
  // friends. Those sections live under Settings now, so the hash is followed there
  // rather than dropped on the floor of a page that has no sections.
  const hash = decodeURIComponent(location.hash.replace(/^#/, ''))
  if (id !== '' && SETTINGS_HASHES.has(hash)) {
    return <Navigate to={`/venues/${id}/settings#${hash}`} replace />
  }

  if (venueQuery.isPending) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (venueQuery.isError || !venueQuery.data) {
    return (
      <PageContainer width="wide" className="space-y-3">
        <ErrorState
          message={venueQuery.isError ? requestErrorMessage(venueQuery.error) : 'That venue could not be loaded.'}
          onRetry={() => void venueQuery.refetch()}
        />
        <Link to="/venues/all" className="text-sm text-ink-muted underline underline-offset-2 hover:text-ink">
          Back to venues
        </Link>
      </PageContainer>
    )
  }

  const venue = venueQuery.data

  return (
    <PageContainer width="wide" className="space-y-6">
      {/*
        Inside a venue the context band above the page already names it over its
        own photograph, and a second hero saying the same thing is the two of them
        arguing. Settings goes with it: the venue's nav carries Settings, so a
        second way in from its own page is a duplicate, not a shortcut.

        From outside — an organization admin browsing venues — there is no band,
        so the page opens on the venue's header and offers the way in.
      */}
      {bandShown ? null : (
        <RecordHeader
          kind="Venue"
          title={venue.name}
          src={venue.coverImageUrl}
          focalPoint={venue.coverFocalPoint}
          facts={venueFacts(venue)}
          actions={
            <>
              <LoginToVenueButton venueId={venue.id} size="sm" fullLabel />
              {seesEvents ? (
                <Button variant="outline" size="sm" onClick={() => navigate(`/venues/${id}/calendar`)}>
                  <CalendarDays className="h-3.5 w-3.5" />
                  Calendar
                </Button>
              ) : null}
              {/* From across the company, the venue's own menu isn't there to reach it by. */}
              {can(user, 'leadStats:view') ? (
                <Button variant="outline" size="sm" onClick={() => navigate(`/venues/${id}/pipeline`)}>
                  <Funnel className="h-3.5 w-3.5" />
                  Pipeline
                </Button>
              ) : null}
              {hasAds ? (
                <Button variant="outline" size="sm" onClick={() => navigate(`/venues/${id}/ads`)}>
                  <Megaphone className="h-3.5 w-3.5" />
                  Ads
                </Button>
              ) : null}
              {seesSettings ? (
                <Button variant="outline" size="sm" onClick={() => navigate(`/venues/${id}/settings`)}>
                  <Settings className="h-3.5 w-3.5" />
                  Settings
                </Button>
              ) : null}
            </>
          }
        />
      )}

      {hasGoHighLevel ? <LeadsPanel venueId={id} /> : null}
      {mayHaveHoneyBook ? <PipelinePanel venueId={id} /> : null}

      {seesEvents ? <VenueEventRail venueId={id} railQuery={railQuery} attention={attention} next={next} /> : null}
    </PageContainer>
  )
}

/** What needs attention, and what's next: the venue's events over the coming days. */
function VenueEventRail({
  venueId: id,
  railQuery,
  attention,
  next,
}: {
  venueId: string
  railQuery: ReturnType<typeof useVenueCalendar>
  attention: ReturnType<typeof attentionItems>
  next: CalendarEventDto[]
}) {
  return (
    <>
      {railQuery.isError ? <FormError>{requestErrorMessage(railQuery.error)}</FormError> : null}

      <div className="grid gap-6 md:grid-cols-2">
        <Card asChild className="space-y-3 p-4">
          <section>
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold">Needs attention</h2>
              <span className="text-xs text-ink-muted">Next {RAIL_DAYS} days</span>
            </div>
            {attention.length === 0 ? (
              <p className="text-sm text-ink-muted">Nothing outstanding in the next {RAIL_DAYS} days.</p>
            ) : (
              <ul className="space-y-2">
                {attention.map((item) => (
                  <li key={`${item.eventId}-${item.reason}`}>
                    <Link
                      to={`/events/${item.eventId}`}
                      className="flex items-start justify-between gap-2 text-sm hover:underline"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{item.name}</span>
                        <span className="flex items-center gap-1 text-xs text-ink-muted">
                          <span className="ds-attention" aria-hidden="true">
                            <TriangleAlert />
                          </span>
                          {ATTENTION_LABELS[item.reason]}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs text-ink-muted">{dayLabel(item.dayKey)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </Card>

        <Card asChild className="space-y-3 p-4">
          <section>
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold">Next up</h2>
              <Link to={`/venues/${id}/calendar`} className="text-xs font-medium text-accent-ink hover:underline">
                Open calendar
              </Link>
            </div>
            {next.length === 0 ? (
              <p className="text-sm text-ink-muted">Nothing booked in the next {RAIL_DAYS} days.</p>
            ) : (
              <ul className="space-y-2">
                {next.map((event) => (
                  <li key={event.id}>
                    <Link
                      to={`/events/${event.id}`}
                      className="flex items-start justify-between gap-2 text-sm hover:underline"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{event.name}</span>
                        <span className="flex items-center gap-2 text-xs text-ink-muted">
                          {/* A time and a count are data: the mono face, tabular. */}
                          <span className="inline-flex items-center gap-1 font-mono tabular-nums">
                            <Clock className="h-3 w-3" />
                            {timeLabel(event.startsAt)}
                          </span>
                          {event.guestCount > 0 ? (
                            <span className="inline-flex items-center gap-1 font-mono tabular-nums">
                              <Users className="h-3 w-3" />
                              {event.guestCount}
                            </span>
                          ) : null}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs text-ink-muted">{dayLabel(event.date)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </Card>
      </div>
    </>
  )
}
